import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, 
  BookOpen, 
  CreditCard, 
  Activity, 
  ShieldAlert, 
  Download,
  Server,
  Zap,
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  Layout,
  Database,
  Lock,
  KeyRound,
  FileCheck,
  Tag,
  ArrowUpRight,
  ShieldCheck,
  Clock,
  CheckCircle2,
  Sparkles,
  DollarSign
} from "lucide-react";
import { motion } from "framer-motion";
import Button from "../../components/common/Button";
import { 
  CompanyPriceListItem, 
  PriceListAnalytics 
} from "../../services/priceListService";
import { usePriceListQuery } from "../../hooks/queries/usePriceListQueries";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [chartView, setChartView] = useState('revenue'); // 'revenue' or 'users'
  const { data: priceListResponse, isLoading: loadingPriceList } = usePriceListQuery();
  const priceListItems = useMemo(
    () => (priceListResponse?.data ? priceListResponse.data.slice(0, 5) : []),
    [priceListResponse]
  );
  const priceAnalytics = priceListResponse?.analytics || null;

  return (
    <div className="cht-space-y-10 cht-animate-in cht-fade-in cht-duration-700">
      
      {/* SuperAdmin Hero */}
      <header className="cht-relative cht-overflow-hidden cht-rounded-[40px] cht-bg-[#1e293b] cht-p-10 md:cht-p-16 cht-text-white">
        <div className="cht-relative cht-z-10 cht-max-w-2xl cht-space-y-6">
          <div className="cht-inline-flex cht-items-center cht-gap-2 cht-px-4 cht-py-1.5 cht-bg-white/10 cht-backdrop-blur-md cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest">
            <ShieldAlert size={12} className="cht-text-emerald-400" /> SuperAdmin Command Console
          </div>
          <h1 className="cht-text-4xl md:cht-text-5xl cht-font-black cht-tracking-tight cht-leading-tight">
            Root Platform <span className="cht-text-[#3e57da]">Infrastructure Center</span>.
          </h1>
          <p className="cht-text-gray-300 cht-font-medium cht-text-lg">
            System status is optimal. All 4 database nodes are synced, 3 security alerts pending review, and 12 instructor payouts queued.
          </p>
          <div className="cht-flex cht-flex-wrap cht-gap-4 cht-pt-4">
            <Button 
              variant="primary" 
              className="cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-rounded-2xl cht-px-8"
              leftIcon={<Download size={18} />}
            >
              Export System Audit
            </Button>
            <Button 
              variant="outline" 
              className="cht-border-white/20 cht-text-white hover:cht-bg-white/10 cht-rounded-2xl cht-px-8"
            >
              Root Security
            </Button>
          </div>
        </div>
        <div className="cht-absolute cht-right-0 cht-top-0 cht-w-1/2 cht-h-full cht-opacity-10 cht-pointer-events-none">
          <Layout className="cht-w-full cht-h-full cht-scale-150 -cht-rotate-12" />
        </div>
      </header>

      {/* Global KPIs */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
        {[
          { label: "Total Revenue (MTD)", value: "₦4,240,500", icon: CreditCard, color: "blue", trend: "+18%" },
          { label: "Platform Users", value: "12,840", icon: Users, color: "purple", trend: "+2.4%" },
          { label: "Server Node Load", value: "34%", icon: Server, color: "emerald", trend: "Optimal" },
          { label: "System Uptime", value: "99.99%", icon: Activity, color: "amber", trend: "Stable" }
        ].map((stat, i) => (
          <GlassCard key={i} className="cht-p-8 cht-rounded-[32px] cht-space-y-4 cht-group hover:cht-border-[#3e57da]/20 cht-transition-all">
            <div className={`cht-w-12 cht-h-12 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-bg-${stat.color}-50 cht-text-${stat.color}-500 group-hover:cht-scale-110 cht-transition-transform`}>
              <stat.icon size={24} />
            </div>
            <div className="cht-space-y-1">
              <div className="cht-flex cht-items-center cht-justify-between">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">{stat.label}</p>
                <span className={`cht-text-[10px] cht-font-black ${stat.trend.includes('+') || stat.trend === 'Optimal' || stat.trend === 'Stable' ? 'cht-text-emerald-500' : 'cht-text-rose-500'}`}>{stat.trend}</span>
              </div>
              <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">{stat.value}</h4>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Analytics & System Health Grid */}
      <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-8">
        
        {/* Dynamic Growth Chart */}
        <GlassCard className="lg:cht-col-span-8 cht-p-10 cht-rounded-[40px] cht-flex cht-flex-col">
          <div className="cht-flex cht-items-center cht-justify-between cht-mb-10">
            <div className="cht-space-y-1">
              <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-tracking-tight">Platform Performance & Revenue Analytics</h3>
              <p className="cht-text-xs cht-text-gray-400 cht-font-medium">SuperAdmin real-time cross-platform telemetry</p>
            </div>
            <div className="cht-flex cht-gap-2 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
              <button 
                onClick={() => setChartView('revenue')}
                className={`cht-px-4 cht-py-2 cht-rounded-lg cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${chartView === 'revenue' ? 'cht-bg-white cht-shadow-sm cht-text-[#3e57da]' : 'cht-text-gray-400 hover:cht-text-gray-600'}`}
              >
                Revenue
              </button>
              <button 
                onClick={() => setChartView('users')}
                className={`cht-px-4 cht-py-2 cht-rounded-lg cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${chartView === 'users' ? 'cht-bg-white cht-shadow-sm cht-text-[#3e57da]' : 'cht-text-gray-400 hover:cht-text-gray-600'}`}
              >
                User Growth
              </button>
            </div>
          </div>

          <div className="cht-h-64 cht-w-full cht-flex cht-items-end cht-gap-3 cht-pt-8 cht-pb-4">
            {[40, 65, 30, 85, 95, 75, 50, 60, 90, 80, 100, 70].map((h, idx) => (
              <div key={idx} className="cht-flex-1 cht-flex cht-flex-col cht-items-center cht-gap-2 cht-h-full cht-justify-end group">
                <div 
                  style={{ height: `${h}%` }}
                  className={`cht-w-full cht-rounded-t-xl cht-transition-all cht-duration-500 ${chartView === 'revenue' ? 'cht-bg-[#3e57da]/80 group-hover:cht-bg-[#3e57da]' : 'cht-bg-purple-500/80 group-hover:cht-bg-purple-500'}`}
                />
                <span className="cht-text-[9px] cht-font-bold cht-text-gray-400">M{idx+1}</span>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Security & System Controls Panel */}
        <GlassCard className="lg:cht-col-span-4 cht-p-10 cht-rounded-[40px] cht-space-y-6">
          <div className="cht-flex cht-items-center cht-justify-between">
            <h3 className="cht-text-xl cht-font-black cht-text-gray-900">Root Health</h3>
            <span className="cht-px-3 cht-py-1 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-full cht-text-[10px] cht-font-black">Active</span>
          </div>

          <div className="cht-space-y-4">
            {[
              { title: "Database Sync", status: "Operational", time: "1 min ago", icon: Database, color: "text-emerald-500" },
              { title: "API Endpoint Latency", status: "24ms avg", time: "Just now", icon: Zap, color: "text-blue-500" },
              { title: "Auth Subsystem", status: "Secured (2FA Active)", time: "5 mins ago", icon: Lock, color: "text-purple-500" },
              { title: "Pending Audits", status: "3 Alerts Flagged", time: "12 mins ago", icon: AlertTriangle, color: "text-amber-500" }
            ].map((item, idx) => (
              <div key={idx} className="cht-p-4 cht-bg-gray-50/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between hover:cht-bg-gray-50 cht-transition-colors">
                <div className="cht-flex cht-items-center cht-gap-3">
                  <div className="cht-p-2.5 cht-bg-white cht-rounded-xl cht-shadow-sm">
                    <item.icon size={18} className={item.color} />
                  </div>
                  <div>
                    <h4 className="cht-text-xs cht-font-black cht-text-gray-900">{item.title}</h4>
                    <p className="cht-text-[10px] cht-font-bold cht-text-gray-400">{item.time}</p>
                  </div>
                </div>
                <span className="cht-text-xs cht-font-extrabold cht-text-gray-700">{item.status}</span>
              </div>
            ))}
          </div>

          <div className="cht-pt-4">
            <Button variant="ghost" className="cht-w-full cht-justify-between cht-bg-gray-50 hover:cht-bg-gray-100 cht-rounded-2xl cht-py-4">
              <span className="cht-text-xs cht-font-black cht-text-gray-700">Open Security Operations Center</span>
              <ChevronRight size={16} className="cht-text-gray-400" />
            </Button>
          </div>
        </GlassCard>

      </div>

      {/* Company Price Catalog & SuperAdmin Governance Center */}
      <GlassCard className="cht-p-8 md:cht-p-10 cht-rounded-[40px] cht-space-y-6">
        <div className="cht-flex cht-flex-col md:cht-flex-row md:cht-items-center cht-justify-between cht-gap-4">
          <div className="cht-flex cht-items-center cht-gap-4">
            <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-indigo-50 cht-text-indigo-600 cht-flex cht-items-center cht-justify-center cht-flex-shrink-0">
              <ShieldCheck size={24} />
            </div>
            <div>
              <div className="cht-flex cht-items-center cht-gap-2">
                <h3 className="cht-text-xl cht-font-black cht-text-gray-900 cht-tracking-tight">Price Catalog Governance</h3>
                <span className="cht-px-2.5 cht-py-0.5 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase">
                  Master Authority
                </span>
              </div>
              <p className="cht-text-xs cht-text-gray-500 cht-font-medium">
                Authoritative pricing controls, margin governance, and pending staff proposal review queue.
              </p>
            </div>
          </div>
          <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3">
            {(priceAnalytics?.total_pending_queue ?? 0) > 0 && (
              <Button
                variant="outline"
                onClick={() => navigate("/superadmin/dashboard/price-list")}
                className="cht-rounded-xl cht-text-xs cht-font-bold cht-border-amber-300 cht-bg-amber-50/50 cht-text-amber-800 hover:cht-bg-amber-100/60 cht-flex cht-items-center cht-gap-1.5"
              >
                <Clock size={14} className="cht-text-amber-600" />
                Review Queue ({priceAnalytics?.total_pending_queue})
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => navigate("/superadmin/dashboard/price-list")}
              className="cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-rounded-xl cht-text-xs cht-font-bold cht-flex cht-items-center cht-gap-1.5"
            >
              Master Price Catalog <ArrowUpRight size={14} />
            </Button>
          </div>
        </div>

        {/* Pending Queue Action Alert if any items are pending */}
        {(priceAnalytics?.total_pending_queue ?? 0) > 0 && (
          <div className="cht-p-4 cht-rounded-2xl cht-bg-gradient-to-r cht-from-amber-500/10 cht-via-indigo-500/5 cht-to-transparent cht-border cht-border-amber-200/60 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-3">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-amber-100 cht-text-amber-700 cht-flex cht-items-center cht-justify-center cht-flex-shrink-0">
                <AlertTriangle size={18} />
              </div>
              <div>
                <p className="cht-text-xs cht-font-black cht-text-amber-900">
                  {priceAnalytics?.total_pending_queue} Rate Proposal{priceAnalytics?.total_pending_queue === 1 ? '' : 's'} Awaiting SuperAdmin Review
                </p>
                <p className="cht-text-[11px] cht-font-medium cht-text-amber-700">
                  {priceAnalytics?.pending_additions ?? 0} new addition{priceAnalytics?.pending_additions === 1 ? '' : 's'}, {priceAnalytics?.pending_updates ?? 0} update{priceAnalytics?.pending_updates === 1 ? '' : 's'}, and {priceAnalytics?.pending_deletions ?? 0} deletion{priceAnalytics?.pending_deletions === 1 ? '' : 's'} staged.
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={() => navigate("/superadmin/dashboard/price-list")}
              className="cht-bg-amber-600 hover:cht-bg-amber-700 cht-border-none cht-rounded-xl cht-text-xs cht-font-bold cht-py-2 cht-px-4 cht-flex-shrink-0"
            >
              Review Now
            </Button>
          </div>
        )}

        {/* Governance Metrics */}
        <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-4 cht-gap-4 cht-pt-1">
          <div className="cht-p-4 cht-bg-gray-50/70 cht-rounded-2xl cht-border cht-border-gray-100">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Active Deliverables</p>
            <p className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-1">
              {loadingPriceList ? "..." : priceAnalytics?.total_live_items ?? 0}
            </p>
          </div>
          <div className="cht-p-4 cht-bg-gray-50/70 cht-rounded-2xl cht-border cht-border-gray-100">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Service Categories</p>
            <p className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-1">
              {loadingPriceList ? "..." : priceAnalytics?.categories?.length ?? 0}
            </p>
          </div>
          <div className="cht-p-4 cht-bg-amber-50/50 cht-rounded-2xl cht-border cht-border-amber-100/60">
            <p className="cht-text-[10px] cht-font-black cht-text-amber-700 cht-uppercase cht-tracking-widest">Pending Proposals</p>
            <p className="cht-text-xl cht-font-black cht-text-amber-900 cht-mt-1">
              {loadingPriceList ? "..." : priceAnalytics?.total_pending_queue ?? 0}
            </p>
          </div>
          <div className="cht-p-4 cht-bg-emerald-50/50 cht-rounded-2xl cht-border cht-border-emerald-100/60">
            <p className="cht-text-[10px] cht-font-black cht-text-emerald-700 cht-uppercase cht-tracking-widest">Price Governance</p>
            <p className="cht-text-xl cht-font-black cht-text-emerald-900 cht-mt-1">Active</p>
          </div>
        </div>

        {/* Master Catalog Preview Table */}
        <div className="cht-overflow-hidden cht-rounded-2xl cht-border cht-border-gray-100 cht-bg-white">
          {loadingPriceList ? (
            <div className="cht-p-8 cht-space-y-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="cht-h-12 cht-bg-gray-100 cht-rounded-xl cht-animate-pulse" />
              ))}
            </div>
          ) : priceListItems.length === 0 ? (
            <div className="cht-p-10 cht-text-center cht-space-y-3">
              <Tag size={32} className="cht-mx-auto cht-text-gray-300" />
              <p className="cht-text-sm cht-font-bold cht-text-gray-700">Master Price Catalog is Empty</p>
              <p className="cht-text-xs cht-text-gray-400">Add service deliverable rates or approve staff proposals to populate the catalog.</p>
              <Button
                variant="primary"
                onClick={() => navigate("/superadmin/dashboard/price-list")}
                className="cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-rounded-xl cht-text-xs cht-font-bold cht-mt-2"
              >
                Manage Price List
              </Button>
            </div>
          ) : (
            <div className="cht-overflow-x-auto">
              <table className="cht-w-full cht-text-left cht-text-xs">
                <thead>
                  <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/50 cht-text-gray-400 cht-font-black cht-uppercase cht-tracking-wider">
                    <th className="cht-py-3.5 cht-px-4">Deliverable / Code</th>
                    <th className="cht-py-3.5 cht-px-4">Category</th>
                    <th className="cht-py-3.5 cht-px-4">Cost Price</th>
                    <th className="cht-py-3.5 cht-px-4">Standard Rate</th>
                    <th className="cht-py-3.5 cht-px-4">Profit Margin</th>
                    <th className="cht-py-3.5 cht-px-4">Governance Status</th>
                    <th className="cht-py-3.5 cht-px-4 cht-text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="cht-divide-y cht-divide-gray-50 cht-font-medium">
                  {priceListItems.map((item) => {
                    const marginVal = parseFloat(item.profit_margin || "0");
                    return (
                      <tr key={item.id} className="hover:cht-bg-gray-50/80 cht-transition-colors">
                        <td className="cht-py-3.5 cht-px-4">
                          <div className="cht-font-black cht-text-gray-900">{item.name}</div>
                          <div className="cht-text-[10px] cht-font-bold cht-text-gray-400">{item.item_code}</div>
                        </td>
                        <td className="cht-py-3.5 cht-px-4">
                          <span className="cht-px-2.5 cht-py-1 cht-bg-gray-100 cht-rounded-md cht-text-[10px] cht-font-bold cht-text-gray-600">
                            {item.category}
                          </span>
                        </td>
                        <td className="cht-py-3.5 cht-px-4">
                          <span className="cht-text-gray-600 font-medium">
                            ₦{Number(item.cost_price || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="cht-py-3.5 cht-px-4">
                          <span className="cht-font-black cht-text-gray-900">
                            ₦{Number(item.standard_price).toLocaleString()}
                          </span>
                        </td>
                        <td className="cht-py-3.5 cht-px-4">
                          <span className={`cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black ${
                            marginVal >= 30 ? 'cht-bg-emerald-50 cht-text-emerald-700' :
                            marginVal >= 15 ? 'cht-bg-blue-50 cht-text-blue-700' :
                            'cht-bg-amber-50 cht-text-amber-700'
                          }`}>
                            {item.profit_margin}%
                          </span>
                        </td>
                        <td className="cht-py-3.5 cht-px-4">
                          {item.change_status && item.change_status !== 'NONE' ? (
                            <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200">
                              {item.change_status_display}
                            </span>
                          ) : item.status === 'APPROVED' ? (
                            <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200 cht-inline-flex cht-items-center cht-gap-1">
                              <CheckCircle2 size={10} /> Authorized
                            </span>
                          ) : (
                            <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200">
                              Requires Review
                            </span>
                          )}
                        </td>
                        <td className="cht-py-3.5 cht-px-4 cht-text-right">
                          <button
                            onClick={() => navigate("/superadmin/dashboard/price-list")}
                            className="cht-text-[#3e57da] hover:cht-text-[#2d41a8] cht-font-bold cht-inline-flex cht-items-center cht-gap-1"
                          >
                            Manage <ChevronRight size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </GlassCard>
    </div>
  );
};

export default SuperAdminDashboard;
